import React from 'react';
import { Form } from 'antd';
import { FormListFieldData } from 'antd/es/form/FormList';
import { PlusCircleOutlined } from '@ant-design/icons';
import { NamePath } from '@xbcb/ui-types';
import { StyledAddButton } from './styles';

export interface FormListSectionHelpers {
  remove: () => void;
  showRemove: boolean;
}

export interface FormListSectionProps {
  /** Array field name path */
  name: NamePath;
  /** Label for the add button, e.g. "Add product identification code" */
  addButtonLabel: string;
  /** Initial value for the list, defaults to [{}] */
  initialValue?: any[];
  /** Minimum number of items allowed, defaults to 1 */
  minItems?: number;
  /** Maximum number of items allowed */
  maxItems?: number;
  /** Read-only mode - hides add/remove buttons */
  readOnly?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Render function for each row in the list */
  renderRow: (
    field: FormListFieldData,
    index: number,
    helpers: FormListSectionHelpers,
  ) => React.ReactNode;
}

/**
 * FormListSection - A reusable wrapper around Ant Design Form.List
 *
 * Handles common boilerplate for repeatable form sections:
 * - Form.List wrapper with proper name path
 * - Add button with consistent styling
 * - Remove logic with minItems enforcement
 * - readOnly/disabled state management
 *
 * @return {JSX.Element} The rendered form list section
 */
const FormListSection: React.FC<FormListSectionProps> = ({
  name,
  addButtonLabel,
  initialValue = [{}],
  minItems = 1,
  maxItems,
  readOnly = false,
  disabled = false,
  renderRow,
}) => {
  return (
    <Form.List name={name} initialValue={initialValue}>
      {(fields, { add, remove }) => {
        const canAdd = !maxItems || fields.length < maxItems;
        const canRemove = fields.length > minItems;

        return (
          <>
            {fields.map((field, index) => {
              const helpers: FormListSectionHelpers = {
                remove: () => remove(field.name),
                showRemove: canRemove && !readOnly,
              };

              return (
                <React.Fragment key={field.key}>
                  {renderRow(field, index, helpers)}
                </React.Fragment>
              );
            })}
            {!readOnly && canAdd && (
              <StyledAddButton
                onClick={() => add()}
                icon={<PlusCircleOutlined />}
                disabled={disabled}
              >
                {addButtonLabel}
              </StyledAddButton>
            )}
          </>
        );
      }}
    </Form.List>
  );
};

export default FormListSection;
